/**
 * Génère une fiche recette en PDF (design JesCook, avec logo).
 */
import { jsPDF } from "jspdf";

const MARGIN = 18;
const PAGE_W = 210; // A4 en mm
const CONTENT_W = PAGE_W - MARGIN * 2;

function loadImageAsDataUrl(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        // Retourner aussi les dimensions pour préserver le ratio
        resolve({
          dataUrl: canvas.toDataURL("image/png"),
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/**
 * @param {Object} recipe
 * @param {Object} options
 */
export async function downloadRecipePdf(recipe, options = {}) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let y = MARGIN;

  const labels = options.labels || {
    prep: "Préparation",
    cook: "Cuisson",
    servings: "Portions",
    difficulty: "Difficulté",
    ingredients: "Ingrédients",
    instructions: "Instructions",
  };

  // Logo JesCook (dans /public/logo/logojes.png) SANS fond noir
  const base = window.location.origin + (process.env.PUBLIC_URL || "");
  const logoUrl = `${base}/logo/logojes.png`;
  const logoInfo = await loadImageAsDataUrl(logoUrl);

  if (logoInfo?.dataUrl) {
    // Logo un peu plus grand pour mieux remplir l'en-tête
    const logoSize = 28;
    doc.addImage(logoInfo.dataUrl, "PNG", MARGIN, 4, logoSize, logoSize);
  } else {
    doc.setFontSize(18);
    doc.setTextColor(251, 146, 60); // orange-400
    doc.text("JesCook", MARGIN, 18);
  }

  // Titre en haut à droite
  doc.setFontSize(11);
  doc.setTextColor(30, 64, 175); // bleu marqué
  doc.setFont("helvetica", "bold");
  doc.text("Fiche recette professionnelle", PAGE_W - MARGIN, 12, {
    align: "right",
  });
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139); // slate-500
  doc.setFont("helvetica", "normal");
  doc.text("Générée avec JesCook", PAGE_W - MARGIN, 17, { align: "right" });

  y = 34;

  // Ligne décorative sous le header
  doc.setDrawColor(251, 146, 60);
  doc.setLineWidth(0.6);
  doc.line(MARGIN, y, PAGE_W - MARGIN, y);
  y += 12;

  // Titre
  doc.setFontSize(22);
  doc.setTextColor(30, 64, 175); // bleu profond
  // Police plus élégante pour un effet \"wahou\"
  doc.setFont("times", "bold");
  const title = recipe.title || recipe.name || "Sans titre";
  const titleLines = doc.splitTextToSize(title, CONTENT_W);
  doc.text(titleLines, MARGIN, y);
  y += titleLines.length * 8 + 4;

  // Description
  if (recipe.description) {
    doc.setFontSize(10);
    doc.setTextColor(55, 65, 81); // slate-700
    doc.setFont("times", "normal");
    const descLines = doc.splitTextToSize(
      String(recipe.description),
      CONTENT_W,
    );
    doc.text(descLines, MARGIN, y);
    y += descLines.length * 5 + 6;
  }

  // Infos temps / portions / difficulté
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139); // slate-500
  const infos = [];
  if (recipe.prep_time) infos.push(`${labels.prep}: ${recipe.prep_time} min`);
  if (recipe.cook_time) infos.push(`${labels.cook}: ${recipe.cook_time} min`);
  if (recipe.servings) infos.push(`${labels.servings}: ${recipe.servings}`);
  if (recipe.difficulty)
    infos.push(`${labels.difficulty}: ${recipe.difficulty}`);
  if (infos.length) {
    doc.text(infos.join("  •  "), MARGIN, y);
    y += 10;
  }

  // Encadré visuel pour image recette (optionnelle)
  const imgUrl = recipe.image_url || recipe.image;
  if (imgUrl) {
    const imgInfo = await loadImageAsDataUrl(imgUrl);
    if (imgInfo?.dataUrl) {
      const maxW = CONTENT_W * 0.58;
      const maxH = 55;
      const ratio = imgInfo.width / imgInfo.height || 1;

      // Calculer largeur/hauteur en respectant le ratio de l'image
      let imgW = maxW;
      let imgH = imgW / ratio;
      if (imgH > maxH) {
        imgH = maxH;
        imgW = imgH * ratio;
      }

      doc.setDrawColor(209, 213, 219); // gris clair
      doc.setFillColor(249, 250, 251); // fond très clair
      doc.rect(MARGIN, y - 2, imgW + 4, imgH + 4, "FD");
      doc.addImage(imgInfo.dataUrl, "PNG", MARGIN + 2, y, imgW, imgH);

      // Encadré infos à droite de l'image
      const infoX = MARGIN + imgW + 10;
      const boxW = PAGE_W - MARGIN - infoX;
      doc.setFillColor(255, 247, 237); // orange très clair
      doc.setDrawColor(251, 146, 60);
      doc.rect(infoX, y - 2, boxW, imgH + 4, "FD");

      let infoY = y + 6;
      doc.setFontSize(11);
      doc.setTextColor(180, 83, 9); // orange foncé
      doc.setFont("helvetica", "bold");
      doc.text("Résumé", infoX + 3, infoY);
      infoY += 7;

      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(55, 65, 81);
      if (infos.length) {
        const infoLines = doc.splitTextToSize(infos.join("  •  "), boxW - 6);
        doc.text(infoLines, infoX + 3, infoY);
        infoY += infoLines.length * 4.5 + 3;
      }

      if (recipe.category || recipe.area) {
        const extra = [
          recipe.category ? `Catégorie : ${recipe.category}` : null,
          recipe.area ? `Origine : ${recipe.area}` : null,
        ].filter(Boolean);
        if (extra.length) {
          const extraLines = doc.splitTextToSize(extra.join("  •  "), boxW - 6);
          doc.text(extraLines, infoX + 3, infoY);
        }
      }

      y += imgH + 12;
    }
  }

  // Ingrédients
  const ingList =
    options.ingredientsList ??
    (recipe.ingredients
      ? String(recipe.ingredients)
          .split("\n")
          .filter((l) => l.trim())
      : []);
  if (ingList.length) {
    doc.setFillColor(240, 253, 244);
    doc.rect(MARGIN, y, CONTENT_W, 10, "F");
    doc.setDrawColor(34, 197, 94);
    doc.rect(MARGIN, y, CONTENT_W, 10, "S");
    doc.setFontSize(12);
    doc.setTextColor(22, 163, 74);
    doc.setFont("times", "bold");
    doc.text(labels.ingredients, MARGIN + 3, y + 7);
    y += 15;

    doc.setFontSize(10);
    doc.setTextColor(50, 50, 50);
    doc.setFont("helvetica", "normal");
    for (const line of ingList) {
      const wrapped = doc.splitTextToSize(`• ${line.trim()}`, CONTENT_W - 6);
      doc.text(wrapped, MARGIN + 4, y);
      y += wrapped.length * 5.5;
    }
    y += 6;
  }

  // Instructions
  const instList =
    options.instructionsList ??
    (recipe.instructions
      ? String(recipe.instructions)
          .split("\n")
          .filter((l) => l.trim())
      : []);
  if (instList.length) {
    if (y > 250) {
      doc.addPage();
      y = MARGIN;
    }
    doc.setFillColor(239, 246, 255); // bleu très clair
    doc.rect(MARGIN, y, CONTENT_W, 10, "F");
    doc.setDrawColor(59, 130, 246); // bleu
    doc.rect(MARGIN, y, CONTENT_W, 10, "S");
    doc.setFontSize(12);
    doc.setTextColor(37, 99, 235); // bleu
    doc.setFont("times", "bold");
    doc.text(labels.instructions, MARGIN + 3, y + 7);
    y += 16;

    doc.setFontSize(10);
    doc.setTextColor(50, 50, 50);
    doc.setFont("helvetica", "normal");
    for (let i = 0; i < instList.length; i++) {
      const step = `${i + 1}. ${instList[i].trim()}`;
      const wrapped = doc.splitTextToSize(step, CONTENT_W - 6);
      if (y + wrapped.length * 5.5 > 285) {
        doc.addPage();
        y = MARGIN;
      }
      doc.text(wrapped, MARGIN + 4, y);
      y += wrapped.length * 5.5;
    }
    y += 8;
  }

  // Footer
  if (y > 270) {
    doc.addPage();
    y = MARGIN;
  }
  y = 285;
  doc.setDrawColor(230, 126, 34);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, y - 5, PAGE_W - MARGIN, y - 5);
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184); // slate-400
  doc.text(
    "JesCook — Fiche recette générée depuis l'application",
    PAGE_W / 2,
    y,
    { align: "center" },
  );
  doc.text(String(title), PAGE_W / 2, y + 5, { align: "center" });

  const safeTitle = String(title)
    .replace(/[^a-zA-Z0-9\u00C0-\u024F\s-]/g, "")
    .replace(/\s+/g, "-")
    .slice(0, 40);
  const fileName = `Fiche-recette-${safeTitle || "recette"}.pdf`;
  doc.save(fileName);
}
